
import { defineComponent, PropType } from 'vue'
import { IconsActionTypes } from '~/store/icons'
import { criticalIcons } from '~/icons/critical'

const SPINNY_ICONS = ['spin-loader']

const SIZES = {
  'xx-small': 'w-2.5 h-2.5',
  'xx-small.5': 'w-[11px] h-[11px]',
  'x-small': 'w-3 h-3',
  'x-13': 'w-[13px] h-[13px]',
  'x-small.5': 'w-3.5 h-3.5',
  small: 'w-4 h-4',
  base: 'w-5 h-5',
  medium: 'w-6 h-6',
  large: 'w-8 h-8'
}

const STROKE_FEATHER = new Set([
  'activity',
  'alert-circle',
  'alert-triangle',
  'arrow-down',
  'arrow-left',
  'arrow-right',
  'arrow-up',
  'arrow-up-right',
  'bar-chart',
  'bar-chart-2',
  'book',
  'book-open',
  'calendar',
  'check',
  'check-circle',
  'chevron-down',
  'chevron-left',
  'chevron-right',
  'chevron-up',
  'chevrons-up',
  'circle',
  'code',
  'corner-up-left',
  'download',
  'edit',
  'edit-2',
  'edit-3',
  'external-link',
  'eye',
  'eye-off',
  'fast-forward',
  'feather',
  'file-plus',
  'file-text',
  'filter',
  'flag',
  'frown',
  'git-commit',
  'git-pull-request',
  'globe',
  'help-circle',
  'home',
  'layers',
  'layout',
  'link-2',
  'log-in',
  'log-out',
  'mail',
  'menu',
  'message-square',
  'minus',
  'minus-circle',
  'more-horizontal',
  'more-vertical',
  'package',
  'paperclip',
  'pause-circle',
  'pie-chart',
  'play-circle',
  'plus',
  'plus-circle',
  'refresh-ccw',
  'refresh-cw',
  'repeat',
  'save',
  'search',
  'send',
  'settings',
  'share',
  'slash',
  'sliders',
  'target',
  'trash',
  'trash-2',
  'trending-up',
  'upload',
  'user',
  'user-check',
  'user-plus',
  'user-x',
  'users',
  'x',
  'x-circle',
  'zap',
  'disc',
  'list',
  'star',
  'info',
  'copy',
  'clipboard'
])

export default defineComponent({
  name: 'ZIcon',
  props: {
    icon: {
      default: '',
      type: String
    },
    size: {
      default: 'small',
      type: String as PropType<keyof typeof SIZES>,
      validator: (val: string) =>
        [
          'x-small',
          'x-small.5',
          'small',
          'base',
          'medium',
          'large',
          'xx-small',
          'xx-small.5',
          'x-13'
        ].includes(val)
    },
    position: {
      default: '',
      type: String
    },
    color: {
      type: String,
      default: 'vanilla-400'
    }
  },

  async fetch() {
    if (this.icon && !this.hasCriticalIcon && this.$store) {
      await this.$store.dispatch(`icons/${IconsActionTypes.FETCH_ICON}`, {
        name: this.icon,
        baseUrl: process.server ? this.$config.bifrostInternalUrl : ''
      })
    }
  },

  fetchOnServer: true,

  computed: {
    hasCriticalIcon(): boolean {
      return this.icon in criticalIcons
    },

    iconContent(): string {
      if (!this.icon) return ''

      if (this.hasCriticalIcon) {
        return criticalIcons[this.icon]
      }

      return this.$store?.state?.icons?.icons?.[this.icon] || ''
    },

    strokeClass(): string {
      if (STROKE_FEATHER.has(this.icon)) {
        return 'stroke-current stroke-2'
      }
      return 'stroke-1.5'
    },

    getSizeStyle(): string {
      return SIZES[this.size] || 'w-5 h-5'
    },

    getPositionStyle(): string {
      if (this.position === 'left') {
        return 'absolute left-2 w-4'
      } else if (this.position === 'right') {
        return 'absolute right-2 w-4'
      }
      return ''
    },

    spinByDefault(): boolean {
      return SPINNY_ICONS.includes(this.icon)
    }
  },

  watch: {
    icon: '$fetch'
  },

  methods: {
    handleClick(event: Event) {
      this.$emit('click', event)
    }
  }
})
